import { useI18n } from '@dh-io-emh/shared/src/composables/i18n.composable';
import { useTracking } from '@dh-io-emh/shared/src/composables/tracking.composable';
import { infuse } from '@dh-io-emh/shared/src/ioc-config';
import {
  FilterCode,
  FilterContainerCode,
  IProductFilterField,
  IProductFilterGroup,
  ProductFilterTypes,
} from '@dh-io-emh/shared/src/models/product-filter/product-filter-base.model';
import { IProductFilterType } from '@dh-io-emh/shared/src/models/product-filter/product-filter.model';
import { useProductsStore } from '@dh-io-emh/shared/src/stores/products/products.composable';
import { Logger } from '@dh-io-emh/shared/src/utils/logger.util';
import { computed, Ref, toRefs, watch } from 'vue';
import { filterGroups, isCategoryFilter } from '../utils/product-filter';

export const isVehicleCategoryFilter = (filter: IProductFilterType) =>
  filter.container?.code === FilterContainerCode.vehicle && filter.code !== FilterCode.stockType;

export const isTransportUsageFilter = (filter: IProductFilterType) =>
  filter.container?.code === FilterContainerCode.transportUsage;

const getVisibleFilterNames = (filters: IProductFilterType[]): string[] =>
  filters.filter(({ priority, visible }) => priority > 0 && visible).map(({ name }) => name);

export const getSelectedFilterCounter = (filters) => {
  return (
    filters
      ?.filter((filter) => filter.isSelected())
      .reduce((acc, subFilters) => {
        if (subFilters.type === ProductFilterTypes.Slider || subFilters.type === ProductFilterTypes.PriceSlider)
          return acc + 1;

        return acc + subFilters.value?.length;
      }, 0) ?? 0
  );
};

const getTrackingActionLabel = ({
  trackingValue,
  filterType,
  filterCode,
  filterCurrentlySelectedItemValue,
  selected,
}: Omit<IProductFilterField, 'filterValue'> & {
  trackingValue?: string;
}): string => {
  if (trackingValue) return `${filterType}|${filterCode}|${trackingValue}|${selected ? 1 : 0}`;

  if (filterType === 'slider' && Array.isArray(filterCurrentlySelectedItemValue)) {
    filterCurrentlySelectedItemValue = filterCurrentlySelectedItemValue.join('-');
  }

  return `${filterType}|${filterCode}|${filterCurrentlySelectedItemValue}|${selected ? 1 : 0}`;
};

export const useFilter = ({
  emit,
  minimal,
  source,
}: {
  emit?: (event: 'all-filters-pressed' | 'change-filter' | 'vehicle-assortment-changed', ...args: any[]) => void;
  minimal?: boolean;
  source?: Ref<IProductFilterType[]>;
} = {}) => {
  const { t } = useI18n();
  const productsStore = useProductsStore();
  const { selectedCategory } = toRefs(productsStore.state);

  const { selectedTab, selectedFilters, selectedVisibleFilters, selectedFiltersFormatted } = toRefs(
    productsStore.selectors,
  );

  const { trackLinkClick, getProductsDataTrackingObject } = useTracking();
  const {
    srp: { availableCategories, products: productsConfig },
  } = infuse('config');

  const filters = computed<IProductFilterType[]>(() => source?.value ?? productsStore.state.filters);

  const categoryFilters = computed<IProductFilterType[]>(() =>
    filters.value.filter(({ visible, code }) => visible && isCategoryFilter(code)),
  );

  const generalFilters = computed<IProductFilterType[]>(() =>
    filters.value.filter((filter: IProductFilterType) => {
      if (minimal) {
        return [FilterCode.priceValue, FilterCode.fuelType, FilterCode.stockType].includes(filter.code as FilterCode);
      }

      if (filter.visible && !isVehicleCategoryFilter(filter) && !isTransportUsageFilter(filter)) {
        if (filter.type === ProductFilterTypes.Slider) {
          // slider filters have a different logic in the BE and will always
          // have a value count of 0. That is why we need to handle that case.
          return true;
        }

        if (!filter.options) {
          return true;
        }

        return filter.options.some((value) => (value.count ?? 0) > 0);
      }

      return false;
    }),
  );

  const vansFilters = computed<IProductFilterType[]>(() =>
    filters.value.filter(
      (filter) => filter.visible && !isVehicleCategoryFilter(filter) && isTransportUsageFilter(filter),
    ),
  );

  const categoryTabNames = computed(() =>
    availableCategories.map((availableCategory) => t(`dcp.srp.filters.${availableCategory.name}.headline`)),
  );

  /**
   * The tabs that are visible in the filter widget underneath the vehicle and body type
   * filters.
   */
  const filterTabs = computed(() => getVisibleFilterNames(generalFilters.value));

  /**
   * The tabs that are visible in the filter widget underneath the vehicle and body type
   * filters.
   */
  const vansFilterTabs = computed(() => getVisibleFilterNames(vansFilters.value));

  /**
   * Array of selected filters in a format required for tracking.
   */
  const filterTrackingValue = computed(() =>
    selectedFiltersFormatted.value.map((filter) => ({
      type: filter.filterType,
      name: filter.filterCode,
      value: filter.filterTrackingValue || filter.filterValue || filter.filterLabel,
    })),
  );

  const openAllFilters = (): void => {
    Logger.debug('FilterWidget::openAllFilters()');
    emit?.('all-filters-pressed');
  };

  const changeFilter = ({
    filterCode,
    filterValue,
    filterType,
    filterCurrentlySelectedItemValue,
    selected,
    trackingValue,
  }: IProductFilterField & {
    trackingValue?: string;
  }) => {
    Logger.debug('FilterWidget::changeFilter()');

    const stop = watch(
      () => productsStore.state.results,
      (curr, prev) => {
        const codes = prev.map((product) => product.code);

        trackLinkClick({
          eventDetail: {
            category: 'filter',
            action: `criteria.${selectedTab.value}`,
            label: getTrackingActionLabel({
              trackingValue,
              filterType,
              filterCode,
              filterCurrentlySelectedItemValue,
              selected,
            }),
            filter: filterTrackingValue.value,
            products: getProductsDataTrackingObject(curr.filter((product) => !codes.includes(product.code))),
          },
        });
        stop();
      },
    );

    const filterChange = { filterCode, filterValue, filterType, filterCurrentlySelectedItemValue, selected };

    emit?.('change-filter', filterChange);
  };

  const categoryTabChanged = ({ detail: clickedCategoryTab }) => {
    if (clickedCategoryTab === selectedCategory.value) {
      return;
    }

    emit?.('vehicle-assortment-changed', clickedCategoryTab);

    const stop = watch(selectedCategory, () => {
      trackLinkClick({
        eventDetail: { category: 'feature', action: 'filter_tab', label: `vehicle_type.${selectedTab.value}` },
      });
      stop();
    });
  };

  const categories: IProductFilterGroup[] = filterGroups.map((category) => ({
    ...category,
    name: t(`dcp.assortment.cars.filtersSearch.filters.category.${category.code}`),
  }));

  // filter categories for legacy horizontal filter, used in filter modal
  const filterCategories = computed<IProductFilterGroup[]>(() => {
    // map filters to category according to "container" property provided by backend
    return categories
      .map((category) => {
        return {
          ...category,
          filters: filters.value.filter((filter) => category.code === filter.container?.code),
        } as IProductFilterGroup;
      })
      .filter((category) => category.filters?.length);
  });

  const getSideFilterCategoryIcon = (
    { code, icon }: { code: string; icon: string },
    selectedTab: Ref<'vans' | 'cars'>,
  ): string => {
    if (code === FilterContainerCode.vehicle && selectedTab.value === 'vans') {
      return 'bds/van-frontal/24';
    }

    return icon;
  };

  // filter categories for sidebar filters first level
  const sideFilterCategories = computed<IProductFilterGroup[]>(() => {
    const visibleFilters = filters.value
      .filter((filter) => filter.visible)
      .filter((filter) => {
        if (filter.values.some((value) => value.selected)) {
          return true;
        }

        if (filter.code === FilterCode.brand && filter.values.length === 1) {
          return false;
        }

        return true;
      });

    let sideFilterCategories = categories.map((category) => {
      return {
        ...category,
        filters: visibleFilters.filter((filter) => category.sideFilters?.includes(filter.code)),
        icon: getSideFilterCategoryIcon(category, selectedTab),
      } as IProductFilterGroup;
    });

    // check if any filter was not mapped to a category
    const unmappedFilters = visibleFilters.filter(
      (filter: IProductFilterType) => !categories.find((category) => category.sideFilters?.includes(filter.code)),
    );

    if (unmappedFilters?.length) {
      // add unmapped filters to category according to "container" property provided by backend (if category exists)
      sideFilterCategories = sideFilterCategories.map((category) => {
        return {
          ...category,
          filters: category.filters.concat(
            unmappedFilters.filter((filter) => category.code === filter.container?.code),
          ),
        } as IProductFilterGroup;
      });
    }

    return sideFilterCategories
      .filter((category) => category.filters?.length)
      .map((category) => {
        return {
          ...category,
          filters: [...category.filters]
            .map((filter) => {
              const { showOnFilters, countThreshold, thresholdTemplate } = productsConfig;

              // do not show count on filters when showOnFilters is false and format count when it is higher than countThreshold
              filter.options = filter.options?.map((option) => {
                option.count =
                  showOnFilters && option.count
                    ? option.count > countThreshold
                      ? thresholdTemplate.replace(/{n}/, String(countThreshold))
                      : option.count
                    : undefined;

                return option;
              });

              return filter;
            })
            .sort(
              ({ priority: firstFilterPriority }, { priority: secondFilterPriority }) =>
                // descending order
                secondFilterPriority - firstFilterPriority,
            ),
        };
      })
      .map((category) => {
        // do not show filter title inside filter category when there is only one filter
        category.hideFilterTitles = category.filters.length === 1;

        return category;
      });
  });

  return {
    availableCategories,
    categoryFilters,
    categoryTabChanged,
    categoryTabNames,
    changeFilter,
    filters,
    filterCategories,
    filterTabs,
    filterTrackingValue,
    generalFilters,
    openAllFilters,
    selectedCategory,
    selectedFilters,
    selectedVisibleFilters,
    vansFilterTabs,
    vansFilters,
    sideFilterCategories,
  };
};
